import React, { type Dispatch, type SetStateAction, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
    ColorNeutralBlack,
    ColorNeutralWhite,
    ColorNordicGrey5,
    FontLineHeightNone,
    Spacing8,
    Spacing12,
    Spacing16
} from 'autogen/design-tokens/tokens';
import type { ChatSectionViewModel } from 'autogen/swagger/all';
import { LiveLabel as LiveLabelText } from 'autogen/translation-keys/trans-website-chat';
import { Button } from 'components/Button';
import mapResponsiveImageModelToPictureProps from 'components/generic/picture/helpers/mappingHelper';
import Picture from 'components/generic/picture/Picture';
import { Typography } from 'components/generic/typography';
import { GA4Tracking } from 'modules/tracking/GA4/GA4Tracking';

const Wrapper = styled.div`
    position: relative;
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    min-width: 1px;
    min-height: 1px;
    background: ${ColorNordicGrey5};
`;

const BackgroundImage = styled(Picture)`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;

    img {
        object-fit: cover;
        object-position: center;
        width: 100%;
        height: 100%;
    }
`;

const ImageContainer = styled.div`
    position: relative;
    left: 0;
    bottom: 0;
    display: flex;
    align-items: flex-end;
    flex: 0 0 28%;
    height: 100%;
    min-width: 1px;
    z-index: 2;    
`;

const StyledPicture = styled(Picture)`  
    max-height: 100%;
`;

const ContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: ${Spacing16};
    flex: 0 0 72%;
    min-height: 175px;
    min-width: 1px;
    padding: ${Spacing16};
    z-index: 3;
`;

const Text = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${Spacing8};
`;

const StyledButton = styled(Button)`
    margin-right: auto;
`;

const LiveLabel = styled.div`
    position: absolute;
    bottom: ${Spacing12};
    display: flex;
    align-items: center;
    padding: 0 ${Spacing12};
    background: ${ColorNeutralBlack};
    height: 30px;
`;

const StyledBody = styled(Typography.Body)`
    font-style: italic;
    text-transform: uppercase;
    letter-spacing: 2px;
    line-height: ${FontLineHeightNone};
    color: ${ColorNeutralWhite};
`;

type ChatSelectContainerProps = {
    section: ChatSectionViewModel;
    minimizeDialog: () => void;
    minimizeChat: () => void;
    setChatOpen: Dispatch<SetStateAction<boolean>>;
    setShowDialog: Dispatch<SetStateAction<boolean>>;
};

export const ChatSelectContainer = (props: ChatSelectContainerProps): JSX.Element => {
    const { section, minimizeDialog, minimizeChat, setChatOpen, setShowDialog } = props;
    const {
        foregroundImage,
        backgroundImage,
        headline,
        description,
        buttonText,
        disabledText,
        isEnabled,
        sectionType,
        isLive,
        botKey,
        communityScriptSrc
    } = section;
    const { t } = useTranslation();

    const loadKindlyScript = () => {
        window.kindlyOptions = {
            position: {
                bottom: '72px',
                right: '14px'
            },
            bubbleHidden: true
        };

        const kindlyScript = document.getElementById('kindly-chat');

        if (!kindlyScript && botKey) {
            const script = document.createElement('script');
            script.src = 'https://chat.kindlycdn.com/kindly-chat.js';
            script.id = 'kindly-chat';
            script.defer = true;
            script.setAttribute('data-shadow-dom', '1');
            script.setAttribute('data-bot-key', botKey);
            document.head.appendChild(script);

            window.addEventListener('kindly:load', () => {
                window.kindlyChat.openChat();
                minimizeDialog();
            });
        }

        // Toggle chat dialog when Kindly is minimized
        window.addEventListener('kindly:ui', (event: any) => {
            if (event.detail.type === 'close-chatbubble') {
                setChatOpen(true);
                setShowDialog(true);
            }

            if (event.detail.type === 'end-chat') {
                minimizeChat();
                kindlyScript?.remove();
            }
        });
    };

    const loadTokyScript = () => {
        const existingScript = document.getElementById('toky-chat');

        if (!existingScript && communityScriptSrc) {
            const script = document.createElement('script');
            script.src = communityScriptSrc;
            script.id = 'toky-chat';
            script.async = true;
            document.head.appendChild(script);

            script.addEventListener('load', () => {
                window.toky.container._loadHtml();
                window.toky.widget.openAnswers();
                minimizeChat();
            });
        }
    };

    async function handleOnClick() {
        if (sectionType === 'Community') {
            loadTokyScript();

            if (window.toky) {
                window.toky.widget.openAnswers();
                minimizeChat();
            }
        } else {
            loadKindlyScript();

            if (window.kindlyChat) {
                window.kindlyChat.openChat();
                minimizeDialog();
            }
        }

        GA4Tracking({
            eventName: 'open_chat',
            context: 'chat',
            category: 'advisory',
            eventParams: [
                { param_name: 'chat_step', param_value: 'step_2' },
                { param_name: 'chat_type', param_value: sectionType }
            ]
        });
    }

    return (
        <Wrapper>
            {backgroundImage && <BackgroundImage {...mapResponsiveImageModelToPictureProps(backgroundImage, 375)} />}
            <ImageContainer>
                {foregroundImage && <StyledPicture {...mapResponsiveImageModelToPictureProps(foregroundImage, 375)} />}
                {isLive && sectionType === 'BeautyExperts' && (
                    <LiveLabel>
                        <StyledBody size="L" strong forwardedAs="span">
                            {t(LiveLabelText)}
                        </StyledBody>
                    </LiveLabel>
                )}
            </ImageContainer>
            <ContentContainer>
                <Text>
                    <Typography.Title size="M">{headline}</Typography.Title>
                    <Typography.Body as="div" size="M">
                        {isEnabled ? description : disabledText}
                    </Typography.Body>
                </Text>
                {isEnabled && (
                    <StyledButton variant="primary" onClick={handleOnClick} size="lg">
                        {buttonText}
                    </StyledButton>
                )}
            </ContentContainer>
        </Wrapper>
    );
};

export default ChatSelectContainer;
